import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Avatar,
  TablePagination
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import api from '../../../services/api';
import { getCurrencySymbol } from '../../../utils/currencyUtils';

const ACTIVITY_LABELS = {
  0: 'Transaction History',
  1: 'Groups',
  2: 'Events',
  3: 'Fundraisers'
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Embeddable panel that displays a single activity type for a user.
 * Designed to be placed inline (not in a Dialog).
 *
 * @param {{ userId: string, userName: string, activityType: number, onClose: () => void }} props
 *   activityType: 0 = Transactions, 1 = Groups, 2 = Events, 3 = Fundraisers
 */
const UserActivityPanel = ({ userId, userName, activityType, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalElements, setTotalElements] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [userId, activityType]);

  useEffect(() => {
    if (userId != null) {
      fetchData();
    }
  }, [userId, activityType, page, rowsPerPage]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    setData([]);

    try {
      const params = { page, size: rowsPerPage };
      let response;

      switch (activityType) {
        case 0:
          response = await api.get(`/v1/api/transactions/user/${userId}`, { params });
          break;
        case 1:
          response = await api.get(`/v1/api/groups/user/${userId}`, { params });
          break;
        case 2:
          response = await api.get(`/v1/api/events/user/${userId}`, { params });
          break;
        case 3:
          response = await api.get(`/v1/api/campaigns/user/${userId}`, { params });
          break;
        default:
          return;
      }

      setData(response.data.content || []);
      setTotalElements(response.data.page?.totalElements || 0);
    } catch (err) {
      console.error('Error fetching user activity:', err);
      setError(err.response?.data?.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  // ── Renderers ────────────────────────────────────────────────────────────

  const renderTransactions = () => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Type</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((tx) => (
            <TableRow key={tx.transactionId} hover>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(tx.transactionDate)}</TableCell>
              <TableCell>
                <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                  {tx.description || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(tx.transactionSource || '').replace(/_/g, ' ')}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={tx.transactionType}
                  size="small"
                  variant="outlined"
                  color={tx.transactionType === 'CREDIT' ? 'success' : 'default'}
                />
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {getCurrencySymbol(tx.currency)} {parseFloat(tx.transactionAmount || 0).toLocaleString()}
              </TableCell>
              <TableCell>
                <Chip
                  label={tx.paymentStatus}
                  size="small"
                  color={
                    tx.paymentStatus === 'PAID' || tx.paymentStatus === 'COMPLETED'
                      ? 'success'
                      : tx.paymentStatus === 'FAILED' ? 'error' : 'default'
                  }
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderGroups = () => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((group) => (
            <TableRow key={group.id || group.groupUuid} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 160 }}>
                  {group.name || group.groupName}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip label={group.groupType || 'N/A'} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                <Chip
                  label={group.groupStatus || group.status || 'N/A'}
                  size="small"
                  color={
                    (group.groupStatus || group.status) === 'ACTIVE' ? 'success'
                      : (group.groupStatus || group.status) === 'SUSPENDED' ? 'error' : 'default'
                  }
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderEvents = () => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((event) => (
            <TableRow key={event.id || event.eventUuid} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 160 }}>
                  {event.title || event.eventTitle}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(event.eventDate || event.startDate)}</TableCell>
              <TableCell>
                <Chip
                  label={event.status || event.eventStatus || 'N/A'}
                  size="small"
                  color={
                    (event.status || event.eventStatus) === 'UPCOMING' ? 'info'
                      : (event.status || event.eventStatus) === 'ONGOING' ? 'success'
                        : (event.status || event.eventStatus) === 'CANCELLED' ? 'error' : 'default'
                  }
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderCampaigns = () => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell align="right">Raised</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((c) => (
            <TableRow key={c.id || c.campaignUuid} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 160 }}>
                  {c.title}
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {getCurrencySymbol(c.currency)} {parseFloat(c.amountRaised || 0).toLocaleString()}
              </TableCell>
              <TableCell>
                <Chip
                  label={c.status || c.campaignStatus || 'N/A'}
                  size="small"
                  color={
                    (c.status || c.campaignStatus) === 'ACTIVE' ? 'success'
                      : (c.status || c.campaignStatus) === 'COMPLETED' ? 'info'
                        : (c.status || c.campaignStatus) === 'SUSPENDED' ? 'error' : 'default'
                  }
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderTable = () => {
    switch (activityType) {
      case 0: return renderTransactions();
      case 1: return renderGroups();
      case 2: return renderEvents();
      case 3: return renderCampaigns();
      default: return null;
    }
  };

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} variant="outlined">
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
            <PersonIcon sx={{ fontSize: 16 }} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {userName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {ACTIVITY_LABELS[activityType]}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>
        ) : data.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No {ACTIVITY_LABELS[activityType].toLowerCase()} found.
            </Typography>
          </Box>
        ) : (
          renderTable()
        )}
      </Box>

      {/* Pagination */}
      {data.length > 0 && (
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10]}
          sx={{ borderTop: 1, borderColor: 'divider', '.MuiTablePagination-toolbar': { minHeight: 40 } }}
        />
      )}
    </Paper>
  );
};

export default UserActivityPanel;
